import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Users, 
  Calendar, 
  Lock, 
  CheckCircle,
  Clock,
  Award,
  AlertTriangle,
  Coins,
  Shield,
  Unlock,
  Info,
  Share2
} from 'lucide-react';
import GroupChat from '@/components/GroupChat';
import GroupAnalytics from '@/components/GroupAnalytics';
import UserAvatar from '@/components/UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AjoGroupDetail = () => {
  const { groupId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [groupDetail, setGroupDetail] = useState(null);
  const [payoutPreview, setPayoutPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [contributing, setContributing] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [showContributeDialog, setShowContributeDialog] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activityFeed, setActivityFeed] = useState([]);
  const [myReferralCode, setMyReferralCode] = useState('');
  const inviteRefCode = (searchParams.get('ref') || '').trim().toUpperCase();

  useEffect(() => {
    fetchGroupDetail();
    fetchUnread();
    fetchActivity();
    fetchReferralCode();
  }, [groupId]);

  const fetchReferralCode = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/referral/info`, { headers: getAuthHeaders() });
      setMyReferralCode(res.data.referral_code || '');
    } catch {}
  };

  const fetchActivity = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ajo/groups/${groupId}/activity`, { headers: getAuthHeaders() });
      setActivityFeed(res.data || []);
    } catch {}
  };

  const fetchUnread = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ajo/groups/unread-counts`, { headers: getAuthHeaders() });
      setUnreadCount(res.data.unread?.[groupId] || 0);
    } catch {}
  };

  const handleChatRead = () => {
    setUnreadCount(0);
  };

  const fetchGroupDetail = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/ajo/groups/${groupId}`, { headers });
      setGroupDetail(response.data);

      // Fetch payout preview if user is a member
      try {
        const previewRes = await axios.get(`${API_URL}/api/ajo/groups/${groupId}/payout-preview`, { headers });
        setPayoutPreview(previewRes.data);
      } catch {
        // User may not be a member yet, that's fine
      }
    } catch (error) {
      toast.error('Failed to load group details');
      navigate('/ajo');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGroup = async () => {
    setJoining(true);
    try {
      await axios.post(`${API_URL}/api/ajo/groups/join`,
        { group_id: groupId, ref_code: inviteRefCode || undefined },
        { headers: getAuthHeaders() }
      );
      toast.success('Successfully joined the group!');
      fetchGroupDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join group');
    } finally {
      setJoining(false);
      setShowJoinDialog(false);
    }
  };

  const handleContribute = async () => {
    setContributing(true);
    try {
      await axios.post(`${API_URL}/api/ajo/groups/contribute`, 
        { group_id: groupId },
        { headers: getAuthHeaders() }
      );
      toast.success('Contribution successful!');
      fetchGroupDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to contribute');
    } finally {
      setContributing(false);
      setShowContributeDialog(false);
    }
  };

  const handleWithdrawDeposit = async () => {
    setWithdrawing(true);
    try {
      await axios.post(`${API_URL}/api/ajo/groups/${groupId}/withdraw-deposit`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Deposit withdrawn successfully! Funds returned to your wallet.');
      navigate('/ajo');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw deposit');
    } finally {
      setWithdrawing(false);
      setShowWithdrawDialog(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('NGN', '₦');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-NG', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>;
      case 'completed':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Completed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPositionStatus = (member, currentCycle, nextPayoutPosition) => {
    if (member.has_received_payout) return 'paid';
    if (member.position === nextPayoutPosition) return 'current';
    return 'upcoming';
  };

  const getPayoutDateForPosition = (position, startDateStr, frequency) => {
    if (!startDateStr || !position) return null;
    try {
      const start = new Date(startDateStr);
      const d = new Date(start);
      if (frequency === 'monthly') {
        d.setDate(d.getDate() + 30 * position);
      } else {
        d.setDate(d.getDate() + 7 * position);
      }
      return d;
    } catch {
      return null;
    }
  };

  const formatShortDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-NG', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleWhatsAppShare = () => {
    if (!groupDetail) return;
    const appUrl = window.location.origin;
    const refSuffix = myReferralCode ? `?ref=${encodeURIComponent(myReferralCode)}` : '';
    const link = `${appUrl}/ajo/${groupId}${refSuffix}`;
    const text =
      `Join my AegisPay Ajo group "${group.name}"!\n\n` +
      `• ${group.group_size} members\n` +
      `• ${formatCurrency(group.contribution_amount)} per ${group.contribution_frequency === 'monthly' ? 'month' : 'week'}\n` +
      `• Total payout: ${formatCurrency(group.total_payout)}\n` +
      `• Lock deposit: ${formatCurrency(group.lock_qualification_amount)}\n\n` +
      `Tap to join: ${link}` +
      (myReferralCode ? `\n\n(Use my invite code: ${myReferralCode})` : '');
    const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pb-24">
        <div className="app-container px-4 pt-6">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-48 w-full rounded-2xl mb-4" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
        <BottomNav />
      </div>
    );
  }

  const { group, members, user_position, next_contribution_date, next_payout_position } = groupDetail;
  const isMember = user_position !== null;
  const isGroupAdmin = group.admin_id === user?.id;
  const canJoin = !isMember && group.status === 'pending' && group.members_count < group.group_size;
  const canContribute = isMember && group.status === 'active';
  const cycleProgress = (group.current_cycle / group.group_size) * 100;

  const daysSinceCreated = Math.floor((Date.now() - new Date(group.created_at).getTime()) / (1000 * 60 * 60 * 24));
  const canWithdrawDeposit = isMember && group.status === 'pending' && daysSinceCreated >= 7;
  const daysUntilWithdraw = Math.max(0, 7 - daysSinceCreated);

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="group-detail-screen">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button 
            onClick={() => navigate('/ajo')}
            className="p-2 rounded-full bg-white shadow-sm hover:shadow-md transition-shadow"
            data-testid="back-button"
          >
            <ArrowLeft size={20} className="text-slate-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-900">{group.name}</h1>
            <p className="text-sm text-slate-500">Created by {group.admin_name}</p>
          </div>
          {getStatusBadge(group.status)}
        </div>

        {/* Invite banner — shown when arrived via a referral link and not yet a member */}
        {inviteRefCode && !isMember && canJoin && (
          <Card className="mb-4 border-2 border-emerald-300 bg-emerald-50 shadow-md" data-testid="invite-banner">
            <CardContent className="p-4 flex items-start gap-3">
              <div className="p-2 rounded-xl bg-emerald-600 flex-shrink-0">
                <Award size={20} className="text-white" />
              </div>
              <div>
                <p className="font-black text-emerald-800 text-base">You were invited to this Ajo!</p>
                <p className="text-sm text-emerald-700 font-semibold mt-1">
                  Invite code <span className="font-black">{inviteRefCode}</span> is attached to your join.
                  When you make your first on-time contribution, your inviter earns Trust Points.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* User Position Banner + Payout Preview */}
        {isMember && (
          <Card className="mb-4 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white" data-testid="user-position-banner">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm text-white font-medium">Your Position</p>
                  <p className="text-3xl font-bold">#{user_position}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-white">
                    {members.find(m => m.position === user_position)?.has_received_payout 
                      ? 'Payout received' 
                      : 'Payout pending'}
                  </p>
                </div>
              </div>

              {/* Payout Breakdown */}
              {payoutPreview && (
                <div className="mt-2 pt-3 border-t border-white/20 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/80">Expected Payout</span>
                    <span className="font-bold">{formatCurrency(payoutPreview.expected_payout)}</span>
                  </div>
                  {payoutPreview.has_security_hold && (
                    <div className="flex justify-between text-sm">
                      <span className="text-amber-200 flex items-center gap-1"><Lock size={12} /> Security Hold</span>
                      <span className="font-bold text-amber-200">-{formatCurrency(payoutPreview.security_hold)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm pt-1 border-t border-white/20">
                    <span className="text-white font-semibold">You Receive</span>
                    <span className="font-black text-lg">{formatCurrency(payoutPreview.net_payout)}</span>
                  </div>
                  {payoutPreview.has_security_hold && (
                    <div className="mt-2 p-2 rounded-lg bg-white/15 flex items-start gap-2">
                      <Unlock size={14} className="text-emerald-300 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-white/90">{payoutPreview.release_info}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Default Warning Banner */}
        {isMember && members.find(m => m.user_id === user?.id)?.status === 'defaulted' && (
          <Card className="mb-4 bg-gradient-to-r from-red-500 to-red-600 text-white border-2 border-red-300" data-testid="default-warning-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle size={28} className="text-white mt-1" />
                <div className="flex-1">
                  <p className="text-lg font-black text-white">⚠️ DEFAULT STATUS</p>
                  <p className="text-sm text-white font-medium mt-1">
                    Your funds are frozen due to missed contributions. Contact support to resolve.
                  </p>
                  <div className="mt-3 p-3 rounded-lg bg-white/20">
                    <p className="text-sm text-white">
                      <span className="font-bold">Reason:</span> {members.find(m => m.user_id === user?.id)?.default_reason || 'Multiple missed contributions'}
                    </p>
                    {members.find(m => m.user_id === user?.id)?.outstanding_debt > 0 && (
                      <p className="text-sm text-white mt-1">
                        <span className="font-bold">Outstanding Debt:</span> {formatCurrency(members.find(m => m.user_id === user?.id)?.outstanding_debt)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Group Stats */}
        <Card className="glass-card mb-4 border-2 border-slate-200 shadow-lg" data-testid="group-stats-card">
          <CardContent className="p-5">
            <h3 className="font-bold text-[#0B1F3A] text-lg mb-4">Group Financial Summary</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-3 rounded-xl bg-blue-50 border-2 border-blue-200">
                <p className="text-sm text-blue-700 font-semibold">{group.frequency === 'monthly' ? 'Monthly' : 'Weekly'} Contribution</p>
                <p className="text-xl font-black text-blue-800">{formatCurrency(group.contribution_amount)}</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50 border-2 border-emerald-200">
                <p className="text-sm text-emerald-700 font-semibold">Total Payout</p>
                <p className="text-xl font-black text-emerald-800">{formatCurrency(group.total_payout)}</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-100 border-2 border-slate-300">
                <p className="text-sm text-slate-700 font-semibold">Group Size</p>
                <p className="text-xl font-black text-slate-800">{group.members_count}/{group.group_size}</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-50 border-2 border-amber-200">
                <p className="text-sm text-amber-700 font-semibold">Lock Deposit</p>
                <p className="text-xl font-black text-amber-800">{formatCurrency(group.lock_qualification_amount)}</p>
              </div>
            </div>

            {/* Cycle Progress */}
            {group.status === 'active' && (
              <div className="mt-4 pt-4 border-t-2 border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-[#0B1F3A]">Cycle Progress</span>
                  <span className="font-black text-[#0B1F3A] text-lg">
                    {group.current_cycle}/{group.group_size}
                  </span>
                </div>
                <Progress value={cycleProgress} className="h-3" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Schedule Info */}
        <Card className="glass-card mb-4 border-2 border-slate-200 shadow-lg" data-testid="schedule-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-xl bg-[#0B1F3A]">
                <Calendar size={22} className="text-white" />
              </div>
              <div>
                <p className="font-bold text-[#0B1F3A] text-lg">Schedule</p>
                <p className="text-slate-700 font-semibold">Start Date: <span className="text-[#0B1F3A] font-black">{formatDate(group.start_date)}</span></p>
              </div>
            </div>
            {next_contribution_date && group.status === 'active' && (
              <div className="p-4 rounded-xl bg-emerald-100 border-2 border-emerald-300">
                <p className="text-emerald-800 font-bold text-base">
                  Next Contribution: <span className="text-emerald-900 font-black">{formatDate(next_contribution_date)}</span>
                </p>
              </div>
            )}

            {/* WhatsApp invite for pending groups with open slots */}
            {group.status === 'pending' && group.members_count < group.group_size && (isMember || isGroupAdmin) && (
              <div className="mt-3">
                <Button
                  type="button"
                  onClick={handleWhatsAppShare}
                  className="w-full h-12 rounded-xl bg-[#25D366] hover:bg-[#20b858] text-white font-bold shadow-md"
                  data-testid="whatsapp-share-btn"
                >
                  <Share2 size={18} className="mr-2" />
                  Invite via WhatsApp
                </Button>
                <p className="text-xs text-slate-500 font-medium mt-2 text-center">
                  Share this group with friends to fill the remaining {group.group_size - group.members_count} slot{group.group_size - group.members_count !== 1 ? 's' : ''}.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Group Analytics */}
        {(isMember || isGroupAdmin) && <GroupAnalytics groupId={groupId} />}

        {/* Members List */}
        <Card className="glass-card mb-4 border-2 border-slate-200 shadow-lg" data-testid="members-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold text-[#0B1F3A] flex items-center gap-2">
              <Users size={22} className="text-[#0B1F3A]" />
              Members & Payout Order
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {members.map((member) => {
                const status = getPositionStatus(member, group.current_cycle, next_payout_position);
                const isCurrentUser = member.user_id === user?.id;
                const payoutDate = getPayoutDateForPosition(member.position, group.start_date, group.contribution_frequency);
                
                return (
                  <div 
                    key={member.user_id}
                    className={`flex items-center gap-3 p-4 rounded-xl ${
                      isCurrentUser ? 'bg-indigo-100 border-2 border-indigo-400' : 'bg-slate-100 border-2 border-slate-200'
                    }`}
                    data-testid={`member-${member.position}`}
                  >
                    <div className="relative">
                      <UserAvatar userId={member.user_id} name={member.user_name} size={40} />
                      <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-white ${
                        status === 'paid' ? 'bg-blue-600' :
                        status === 'current' ? 'bg-emerald-600' : 'bg-slate-500'
                      }`}>
                        {member.position}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-slate-900 text-base">
                        {member.user_name}
                        {isCurrentUser && <span className="text-indigo-700 font-black ml-1">(You)</span>}
                        {member.is_verified && (
                          <span className="inline-flex items-center gap-0.5 ml-1.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold align-middle">
                            <CheckCircle size={10} /> Verified
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-slate-600 font-semibold">
                        {member.contributions_made} contributions made
                      </p>
                      {payoutDate && (
                        <p className="text-xs font-bold text-[#0B1F3A] mt-1 flex items-center gap-1" data-testid={`member-payout-date-${member.position}`}>
                          <Calendar size={11} className="text-[#2272C5]" />
                          {status === 'paid'
                            ? <>Paid on <span className="font-black">{formatShortDate(payoutDate)}</span></>
                            : <>Payout: <span className="font-black">{formatShortDate(payoutDate)}</span></>}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      {status === 'paid' && (
                        <Badge className="bg-blue-600 text-white font-bold">Paid</Badge>
                      )}
                      {status === 'current' && (
                        <Badge className="bg-emerald-600 text-white font-bold">Next Payout</Badge>
                      )}
                      {member.locked_security_amount > 0 && (
                        <div className="flex items-center gap-1 mt-1 text-sm text-amber-700 font-bold">
                          <Lock size={14} />
                          {formatCurrency(member.locked_security_amount)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Empty slots */}
              {Array.from({ length: group.group_size - members.length }).map((_, idx) => (
                <div 
                  key={`empty-${idx}`}
                  className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border-2 border-dashed border-slate-300"
                >
                  <div className="position-badge position-upcoming">
                    {members.length + idx + 1}
                  </div>
                  <p className="text-base text-slate-500 font-semibold">Waiting for member...</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Activity Feed */}
        {activityFeed.length > 0 && (
          <Card className="glass-card mb-6" data-testid="group-activity-feed">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2">
                <Clock size={18} className="text-[#2272C5]" />Group Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {activityFeed.slice(0, 15).map((a) => (
                  <div key={a.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-slate-50">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${a.type === 'contribution' ? 'bg-emerald-100' : a.type === 'join' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                      {a.type === 'contribution' ? <CheckCircle size={14} className="text-emerald-600"/> : a.type === 'join' ? <Users size={14} className="text-blue-600"/> : <Award size={14} className="text-amber-600"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700">{a.message}</p>
                      <p className="text-[10px] text-slate-400">{new Date(a.created_at).toLocaleString('en-NG', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fund Safety Info */}
        <Card className="glass-card mb-6 border-2 border-[#0B1F3A]/20 shadow-lg" data-testid="security-info-card">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-[#0B1F3A]">
                <Shield size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="font-black text-[#0B1F3A] text-lg">How Your Funds Are Protected</p>
                <ul className="mt-3 space-y-3">
                  <li className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <Shield size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-emerald-800 font-semibold text-sm">
                      <span className="font-black">Qualification Deposit:</span> A commitment deposit (50% of payout) is held to protect all members. It is returned in full when the group completes.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <Lock size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <span className="text-amber-800 font-semibold text-sm">
                      <span className="font-black">Security Hold:</span> Members who receive their payout early may have a small amount held temporarily. This protects the group and is released automatically as the cycle progresses.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 p-3 rounded-xl bg-blue-50 border border-blue-200">
                    <Unlock size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-blue-800 font-semibold text-sm">
                      <span className="font-black">Automatic Release:</span> All locked funds are released automatically when conditions are met. No manual action is required.
                    </span>
                  </li>
                </ul>
                <div className="mt-4 p-3 rounded-xl bg-[#0B1F3A]/5 border border-[#0B1F3A]/10 flex items-center gap-2">
                  <Info size={14} className="text-[#2272C5] flex-shrink-0" />
                  <span className="text-xs text-slate-600">Your money is always yours. Locks exist only to ensure every member fulfils their commitment to the group.</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {canJoin && (
          <Button
            onClick={() => setShowJoinDialog(true)}
            className="w-full h-16 rounded-2xl bg-[#2272C5] hover:bg-[#1a5da8] text-white font-bold text-lg shadow-xl btn-press"
            disabled={joining}
            data-testid="join-group-button"
          >
            <Lock size={22} className="mr-2" />
            Join Group ({formatCurrency(group.lock_qualification_amount)} lock deposit)
          </Button>
        )}

        {canContribute && (
          <Button
            onClick={() => setShowContributeDialog(true)}
            className="w-full h-16 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg shadow-xl btn-press"
            disabled={contributing}
            data-testid="contribute-button"
          >
            <Coins size={22} className="mr-2" />
            Make Contribution ({formatCurrency(group.contribution_amount)})
          </Button>
        )}

        {/* Withdraw Deposit Section */}
        {isMember && group.status === 'pending' && (
          <Card className="mt-4 glass-card border-2 border-slate-200" data-testid="withdraw-deposit-section">
            <CardContent className="p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 rounded-xl bg-amber-100">
                  <Info size={16} className="text-amber-600" />
                </div>
                <div>
                  <p className="font-bold text-[#0B1F3A] text-sm">Group Not Yet Full?</p>
                  <p className="text-xs text-slate-600 mt-1">
                    If this group hasn't filled up within 7 days of creation, you can withdraw your lock qualification deposit back to your available balance. You will be removed from the group.
                  </p>
                </div>
              </div>
              {canWithdrawDeposit ? (
                <Button
                  onClick={() => setShowWithdrawDialog(true)}
                  variant="outline"
                  className="w-full h-12 rounded-xl border-2 border-red-300 text-red-600 hover:bg-red-50 font-bold"
                  disabled={withdrawing}
                  data-testid="withdraw-deposit-button"
                >
                  <Unlock size={18} className="mr-2" />
                  Withdraw Deposit ({formatCurrency(group.lock_qualification_amount)})
                </Button>
              ) : (
                <div className="p-3 rounded-xl bg-slate-100 text-center" data-testid="withdraw-countdown">
                  <p className="text-sm text-slate-600 font-semibold">
                    <Clock size={14} className="inline mr-1 -mt-0.5" />
                    Withdrawal available in <span className="font-black text-[#0B1F3A]">{daysUntilWithdraw} day{daysUntilWithdraw !== 1 ? 's' : ''}</span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {group.status === 'completed' && (
          <div className="p-5 rounded-2xl bg-emerald-100 border-2 border-emerald-400 text-center shadow-lg">
            <CheckCircle size={32} className="mx-auto text-emerald-700 mb-2" />
            <p className="font-black text-emerald-800 text-lg">Ajo Group Completed!</p>
            <p className="text-emerald-700 font-semibold">All payouts distributed. Lock deposits released.</p>
          </div>
        )}
      </div>

      {/* Join Dialog */}
      <AlertDialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0B1F3A] font-black text-xl">Join {group.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-4 mt-2">
                <p className="text-slate-700 font-semibold">To join this group, you need to pay a lock qualification deposit:</p>
                <div className="p-5 rounded-xl bg-[#0B1F3A] text-center">
                  <p className="text-3xl font-black text-white">
                    {formatCurrency(group.lock_qualification_amount)}
                  </p>
                  <p className="text-blue-200 font-semibold mt-1">50% of total payout (Required commitment)</p>
                </div>
                <div className="p-4 rounded-xl bg-emerald-100 border-2 border-emerald-300">
                  <p className="font-black text-emerald-800 text-base mb-2">What happens to this deposit?</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-700 font-bold">•</span>
                      <span className="text-emerald-800 font-semibold">Funds are locked to protect all group members</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-700 font-bold">•</span>
                      <span className="text-emerald-800 font-semibold">Released automatically when the group completes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-700 font-bold">•</span>
                      <span className="text-emerald-800 font-semibold">This is a protection mechanism, not a penalty</span>
                    </li>
                  </ul>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-semibold border-2">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleJoinGroup}
              disabled={joining}
              className="bg-[#2272C5] hover:bg-[#1a5da8] font-bold"
            >
              {joining ? 'Joining...' : 'Confirm & Join'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Contribute Dialog */}
      <AlertDialog open={showContributeDialog} onOpenChange={setShowContributeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0B1F3A] font-black text-xl">Make Contribution</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-4 mt-2">
                <p className="text-slate-700 font-semibold">You are about to make your {group.frequency === 'monthly' ? 'monthly' : 'weekly'} contribution:</p>
                <div className="p-5 rounded-xl bg-[#0B1F3A] text-center">
                  <p className="text-3xl font-black text-white">
                    {formatCurrency(group.contribution_amount)}
                  </p>
                  <p className="text-blue-200 font-semibold mt-1">{group.frequency === 'monthly' ? 'Monthly' : 'Weekly'} contribution</p>
                </div>
                {user_position && user_position <= Math.floor(group.group_size / 2) && !members.find(m => m.position === user_position)?.has_received_payout && (
                  <div className="p-4 rounded-xl bg-amber-100 border-2 border-amber-400">
                    <p className="font-bold text-amber-800 text-base mb-1">Note for Position {user_position}:</p>
                    <p className="text-amber-700 font-semibold text-sm">
                      When you receive your payout, a security hold will apply. This is automatically released at Position {Math.floor(group.group_size / 2) + 1}.
                    </p>
                  </div>
                )}
                <p className="text-slate-700 font-semibold">
                  Once all members contribute, the next position will receive their payout.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-semibold border-2">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleContribute}
              disabled={contributing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
            >
              {contributing ? 'Processing...' : 'Confirm Contribution'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Withdraw Deposit Dialog */}
      <AlertDialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 font-black text-xl">Withdraw Deposit?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 mt-2">
                <span className="text-slate-700 font-semibold block">You are about to withdraw your lock qualification deposit:</span>
                <div className="p-5 rounded-xl bg-red-50 border-2 border-red-200 text-center">
                  <span className="text-3xl font-black text-red-600 block">
                    {formatCurrency(group.lock_qualification_amount)}
                  </span>
                  <span className="text-red-400 font-semibold mt-1 block">Will be returned to your available balance</span>
                </div>
                <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-200">
                  <span className="font-bold text-amber-800 text-sm mb-2 block">Please note:</span>
                  <ul className="space-y-1.5">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-700 font-bold">•</span>
                      <span className="text-amber-700 font-semibold text-sm">You will be removed from this group</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-700 font-bold">•</span>
                      <span className="text-amber-700 font-semibold text-sm">Your position will no longer be reserved</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-700 font-bold">•</span>
                      <span className="text-amber-700 font-semibold text-sm">This action cannot be undone</span>
                    </li>
                  </ul>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-semibold border-2">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleWithdrawDeposit}
              disabled={withdrawing}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
            >
              {withdrawing ? 'Processing...' : 'Confirm Withdrawal'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Group Chat - for members and group admin */}
      {(isMember || isGroupAdmin) && <GroupChat groupId={groupId} unreadCount={unreadCount} onRead={handleChatRead} />}

      <BottomNav />
    </div>
  );
};

export default AjoGroupDetail;
